import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { hasPermission } from './config/permissions';
import { isSuperUser } from './utils/roleUtils';
import {
    collection,
    onSnapshot,
    query,
    addDoc,
    where,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    writeBatch,
    orderBy,
    arrayRemove
} from './firestore-compat';
import {
    addDays,
    addWeeks,
    addMonths,
    isBefore,
    isEqual,
    format
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AddAuditoriaModal from './AddAuditoriaModal';
import { notifyParticipantsOfChange } from './utils/changeNotificationUtils';
import AgendaQualidade from './AgendaQualidade';
import './MinhaAgenda.css';
import { useTemaTela, SeletorTema, textoPara } from './components/TemaTela';

const generateWeekdayGrid = (date) => {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    let dayOfWeek = firstDayOfMonth.getDay();
    let diff = firstDayOfMonth.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const gridStartDate = new Date(firstDayOfMonth.setDate(diff));

    const monthDays = [];
    let currentDate = new Date(gridStartDate);

    for (let i = 0; i < 35; i++) {
        monthDays.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
        if (currentDate.getDay() === 6) {
            currentDate.setDate(currentDate.getDate() + 2);
        }
    }
    return monthDays;
};

function MinhaAgenda() {
    const [currentDate, setCurrentDate] = useState(new Date());

    // Hook para detectar mobile
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [eventos, setEventos] = useState([]);
    const [userData, setUserData] = useState(null);
    const [allUsersConfig, setAllUsersConfig] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvento, setEditingEvento] = useState(null);
    const [isAuditor, setIsAuditor] = useState(false);
    const [filtroRealizar, setFiltroRealizar] = useState(false);
    const [filtroNaoRealizados, setFiltroNaoRealizados] = useState(false);
    const [filtroOrigem, setFiltroOrigem] = useState('Todos');
    // cores escolhidas pela pessoa (botão 🎨 Cores)
    const { tema, salvar: salvarTema } = useTemaTela('agenda');

    const currentUser = auth.currentUser;

    const monthDays = generateWeekdayGrid(currentDate);

    // Memoize empty array to prevent re-renders
    const emptyUnidadesConfig = useMemo(() => [], []);

    useEffect(() => {
        if (!currentUser) return;

        const fetchData = async () => {
            // 1. Get user data & All Users for selection
            const qU = query(collection(db, 'users'), where('email', '==', currentUser.email));
            const snapU = await getDocs(qU);

            // Fecth all users for the "Responsáveis" list in the modal
            const qAllUsers = query(collection(db, 'users'), orderBy('name'));
            const snapAllUsers = await getDocs(qAllUsers);
            const allUsersList = snapAllUsers.docs.map(d => ({
                id: d.id,
                name: d.data().name || d.data().email,
                color: d.data().color || '#00897b' // Fallback color
            }));
            setAllUsersConfig(allUsersList);

            if (snapU.empty) {
                setLoading(false);
                return;
            }
            const uData = snapU.docs[0].data();
            setUserData(uData);

            // Verifica se o usuário é auditor ou admin
            const hasAuditorPerm = uData.permissions?.agenda_qualidade?.edit === true;
            const roleList = Array.isArray(uData.roles) ? uData.roles : (uData.role ? [uData.role] : []);
            const isAdm = roleList.includes('admin') || roleList.includes('diretoria') || roleList.includes('diretor');

            setIsAuditor(hasAuditorPerm || isAdm);

            const myAgendaName = uData.agendaName || uData.name;

            // 2. Collections to watch
            // MUDANÇA ARQUITETURAL: Ler APENAS as coleções onde o usuário tem controle total (cópia local)
            // Isso elimina erros de permissão ao tentar excluir/editar eventos de terceiros
            // 2. Collections to watch
            // MUDANÇA ARQUITETURAL: Sincronia Virtual (Clone)
            // Lemos de várias fontes e unificamos na tela.
            // O usuário vê TUDO que ele participa, independente de onde foi criado.
            const collectionsToWatch = [
                // 1. Minha Agenda Pessoal (Eventos privados + Cópias manuais)
                { name: getCollectionName('agenda_pessoal'), label: 'Pessoal', participantField: 'auditores', permission: true },

                // 2. Agenda da Direção (Se for participante/diretor)
                { name: getCollectionName('agendaDirecao'), label: 'Direção', participantField: 'participantes', permission: true },

                // 3. Agenda da Qualidade (Se for auditor/participante)
                { name: getCollectionName('auditorias'), label: 'Qualidade', participantField: 'auditores', permission: true }
            ];

            const unsubscribes = collectionsToWatch.map(coll => {
                // Se for coleção "pública" (Direção/Qualidade), traz só o que sou participante
                // Se for pessoal, traz tudo (já é filtrado por rules, mas garantimos aqui)

                // NOTA: Firestore Rules já bloqueiam leitura de documentos que não somos donos/participantes
                // Então podemos tentar ler a coleção filtrando por status, e o security rule vai 'peneirar'
                // ERROR CATCH: Se der erro de permissão (ex: não sou participante de NADA na direção), o snapshot falha.
                // SOLUÇÃO: Usar queries que batem com as Rules.

                let q;
                const agendaPessoalName = getCollectionName('agenda_pessoal');
                const agendaDirecaoName = getCollectionName('agendaDirecao');

                if (coll.name === agendaPessoalName) {
                    // FIX: Filtrar por createdBy para satisfazer a regra de segurança do Firestore
                    // A regra "match /agenda_pessoal/{doc}" exige "resource.data.createdBy == request.auth.uid"
                    // Portanto a query PRECISA ter esse filtro também.
                    q = query(
                        collection(db, coll.name),
                        where('createdBy', '==', currentUser.uid)
                    );
                } else if (coll.name === agendaDirecaoName) {
                    // MUDANÇA: Query inteligente baseada em permissão
                    // Normalizar roles: array ou string legada
                    const userRoles = Array.isArray(uData.roles) ? uData.roles
                        : (uData.role ? [uData.role] : []);
                    // 1. Se tem permissão de VER TUDO (Diretoria, Secretária, etc)
                    if (hasPermission(userRoles, 'view_agenda_direcao', uData.permissions) || userRoles.includes('admin')) {
                        q = query(collection(db, coll.name));
                    } else {
                        // 2. Se NÃO tem permissão de ver tudo, vê apenas onde é PARTICIPANTE
                        // Isso evita o erro de permissão "Missing Permissions" ao tentar ler a coleção inteira
                        q = query(
                            collection(db, coll.name),
                            where('participantesUids', 'array-contains', currentUser.uid)
                        );
                    }
                    // OU se for usuario comum, ele só vai conseguir ler se fizer a query EXATA que o rule permite?
                    // R: Rules não filtram query results, elas bloqueiam a query se ela pedir algo que vc não pode ver.
                    // SE o usuário tem 'view_agenda_direcao', ele vê tudo.
                    // SE NÃO, ele só vê o que é participante.

                    // ESTRATÉGIA HÍBRIDA:
                    // Se tiver permissão global, lê tudo e filtra na memória.
                    // Se não, tenta ler apenas os seus.
                } else {
                    q = query(collection(db, coll.name));
                }

                return onSnapshot(q, (snapshot) => {
                    const docs = snapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        sourceColl: coll.name,
                        sourceLabel: coll.label,
                        participantField: coll.participantField
                    }));

                    setEventos(prev => {
                        // ... Lógica de merge mantida, apenas atualizando a fonte ...
                        const ignoredInvites = JSON.parse(localStorage.getItem('ignored_invites') || '{}');
                        const otherColls = prev.filter(item => item.sourceColl !== coll.name);

                        const filtered = docs.filter(item => {
                            if (ignoredInvites[item.id]) return false;

                            // Ignorar cópias externas EXCETO quando vêm da agendaDirecao (são eventos legítimos da Direção)
                            if (item.isExternalCopy && coll.name !== getCollectionName('agendaDirecao')) return false;

                            // CRITÉRIO DE VISIBILIDADE DO CLONE:
                            // 2. Sou participante (por nome ou UID)?
                            // MUDANÇA: Só mostrar se EU CRIEI ou se JÁ ACEITEI (para evitar duplicidade visual com a aba 'Convites')
                            // Exceção: Agenda Pessoal sempre motra (pois é minha)

                            let participants = item[coll.participantField] || [];
                            if (!Array.isArray(participants)) {
                                participants = typeof participants === 'string' ? participants.split(',').map(s => s.trim()) : [];
                            }

                            let uids = item.participantesUids || [];
                            if (!Array.isArray(uids)) {
                                uids = typeof uids === 'string' ? uids.split(',').map(s => s.trim()) : [];
                            }

                            const isCreator = item.criadoPorUid === currentUser.uid || item.ownerUid === currentUser.uid || item.createdBy === currentUser.uid;

                            // MUDANÇA: Aliases para o usuário atual (Oliver = Alexandre)
                            const safeMyName = myAgendaName || '';
                            const aliases = [safeMyName];
                            const safeEmail = currentUser.email?.toLowerCase();
                            if (safeEmail === 'alexandrebareaoliveira@gmail.com' || safeMyName.includes('Oliver') || safeMyName.includes('Alexandre')) {
                                aliases.push('Oliver', 'Alexandre', 'Oliver Barea');
                            }

                            const isAccepted = item.aceitePorUids?.includes(currentUser.uid) ||
                                (item.aceitePor && item.aceitePor.some(a => aliases.some(alias => a.includes(alias))));

                            // Se for agenda pessoal, assumimos que tudo que está lá é "aceito" ou meu.
                            const isPersonalColl = coll.name === getCollectionName('agenda_pessoal');

                            // Para agendaDirecao: se o diretor está em 'responsaveis', ele DEVE ver o evento
                            // MUDANÇA: Apenas diretores (Ana Paula, Dr. Mário, Diretoria) vêem sem aceite.
                            // Participantes comuns (Auditores, etc) precisam aceitar via aba 'Convites'.
                            const isDirecaoResponsavel = coll.name === getCollectionName('agendaDirecao') && (
                                (item.responsaveis || []).some(r => typeof r === 'string' && aliases.some(alias => r.includes(alias) || alias.includes(r)))
                            );

                            // Para auditorias: se o auditor está designado no evento, ele DEVE ver
                            const isAuditorDesignado = coll.name === getCollectionName('auditorias') && (
                                (item.auditores || []).some(a => typeof a === 'string' && aliases.some(alias => a.includes(alias) || alias.includes(a))) ||
                                (item.participantesUids || []).includes(currentUser.uid)
                            );

                            if (!isPersonalColl && !isCreator && !isAccepted && !isDirecaoResponsavel && !isAuditorDesignado) {
                                return false; // Ainda é um convite pendente, não mostrar na agenda principal
                            }

                            const isMyName = participants.some(p => typeof p === 'string' && aliases.some(alias => p.includes(alias) || alias.includes(p)));
                            const isMyUid = uids.includes(currentUser.uid);
                            const isOwner = item.createdBy === currentUser.uid;

                            // Se for agenda pessoal, mostra tudo (já filtrei na query/rules)
                            // FIX: Filtrar proativamente para não confiar apenas na Rule (caso Admin)
                            // Se "Oliver" estiver vendo dados do "Mario", este filtro resolve.
                            if (coll.name === getCollectionName('agenda_pessoal')) {
                                return item.createdBy === currentUser.uid || item.uid === currentUser.uid;
                            }

                            // Se for Direção: mostrar se sou responsável ou se já aceitei (agendado)
                            if (coll.name === getCollectionName('agendaDirecao')) {
                                const DIRECAO_NAMES = ['Ana Paula', 'Dr. Mário', 'Diretoria'];
                                const isDirector = DIRECAO_NAMES.some(d => d.includes(myAgendaName) || myAgendaName.includes(d));

                                // Se for o diretor responsável, vê sempre. Se for outro participante, vê se aceitou.
                                return isDirector ? (isMyName || isMyUid || isOwner || isDirecaoResponsavel) : (isAccepted || isOwner);
                            }

                            // Se for Qualidade/Outros, SÓ MOSTRA SE EU FOR PARTICIPANTE ou dono
                            return isMyName || isMyUid || isOwner;
                        });

                        // Deduplicação por ID e Textual: se o mesmo evento vem de múltiplas coleções,
                        // ou se há clones físicos, mapear pela chave mais forte.
                        const allEvents = [...otherColls, ...filtered].sort((a, b) => (a.horario || '').localeCompare(b.horario || ''));
                        const seen = new Map();
                        const priorityOrder = {
                            [getCollectionName('agenda_pessoal')]: 1,
                            [getCollectionName('agendaDirecao')]: 2,
                            [getCollectionName('auditorias')]: 3
                        };
                        for (const ev of allEvents) {
                            // Usar o ID original se for uma cópia explícita
                            const baseId = ev.isExternalCopy && ev.ownerUid ? ev.ownerUid : (ev.id.startsWith('ext-') ? ev.id.replace('ext-', '') : ev.id);

                            // Criar uma chave textual agressiva (exatamente como no AdminEventos/AgendaQualidade)
                            const cleanDate = (ev.data || 'nodate').trim();
                            const cleanTime = (ev.horario || 'notime').trim();
                            let text = (ev.descricao || ev.auditoria || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                            text = text.substring(0, 25);

                            // A chave de deduplicação é o texto/hora (muito forte) ou o ID base se não tiver texto bom
                            const dedupKey = text.length > 5 ? `${cleanDate}_${cleanTime}_${text}` : baseId;

                            const existing = seen.get(dedupKey);
                            if (!existing) {
                                seen.set(dedupKey, ev);
                            } else {
                                // Manter o de maior prioridade (menor número)
                                const existingPri = priorityOrder[existing.sourceColl] || 99;
                                const newPri = priorityOrder[ev.sourceColl] || 99;
                                if (newPri < existingPri) {
                                    seen.set(dedupKey, ev);
                                }
                            }
                        }
                        return Array.from(seen.values());
                    });
                    setLoading(false);
                }, (error) => {
                    // Silenciar erros de permissão (significa que não tenho acesso àquela agenda, ok)
                    console.log(`Sem acesso a ${coll.label} (Normal para usuários restritos):`, error.code);
                    setLoading(false);
                });
            });

            return () => unsubscribes.forEach(unsub => unsub());
        };

        fetchData();

        // Mantivemos apenas o `fetchData` que lida com a agenda em si.
    }, [currentUser]);

    const handlePrevMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1, 1)));
    const handleNextMonth = () => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1, 1)));

    const handleAddEvento = async (newEvento) => {
        try {
            const myAgendaName = userData?.agendaName || userData?.name || currentUser.displayName || currentUser.email;
            const { publicarQualidade, publicarHomecare, ...eventoBase } = newEvento;

            const baseFields = {
                ...eventoBase,
                auditor: myAgendaName,
                auditores: [...new Set([...(eventoBase.auditores || []), myAgendaName])],
                createdBy: currentUser.uid,
                criadoPorUid: currentUser.uid,
                criadoPorNome: myAgendaName,
                status: 'Agendado',
            };

            // Sempre salva na agenda pessoal
            await addDoc(collection(db, getCollectionName('agenda_pessoal')), {
                ...baseFields,
                publicarQualidade: !!publicarQualidade,
                publicarHomecare: !!publicarHomecare,
            });

            // Cross-post para Agenda da Qualidade
            if (publicarQualidade) {
                await addDoc(collection(db, getCollectionName('auditorias')), {
                    ...baseFields,
                    sourceAgendaPessoal: true,
                    conviteSistema: false,
                });
            }

            // Cross-post para Agenda Homecare pública (auditorias_homecare)
            if (publicarHomecare) {
                await addDoc(collection(db, getCollectionName('auditorias_homecare')), {
                    ...baseFields,
                    auditores: baseFields.auditores,
                    reagendadoVezes: 0,
                    sourceAgendaPessoal: true,
                    publicadoPor: myAgendaName,
                    publicadoEm: new Date().toISOString(),
                });
            }
        } catch (error) {
            console.error("Erro ao adicionar evento pessoal:", error);
            alert("Erro ao salvar: " + (error.message || error));
        }
    };



    const handleUpdateEvento = async (eventoOriginal, dadosAtualizados) => {
        const coll = eventoOriginal.sourceColl || getCollectionName('agenda_pessoal');
        const isAuditor = userData?.permissions?.agenda_qualidade?.edit === true;

        // Permitir edição se for:
        // - Agenda pessoal
        // - Auditoria (se for auditor)
        // - Cópia de participante (isParticipantCopy)
        const podeEditar = coll === getCollectionName('agenda_pessoal') ||
            (coll === getCollectionName('auditorias') && isAuditor) ||
            eventoOriginal.isParticipantCopy === true ||
            // NOVO: Se sou participante (Uid na lista), posso editar via Regra de Segurança
            (eventoOriginal.participantesUids && eventoOriginal.participantesUids.includes(currentUser.uid));

        if (!podeEditar) {
            alert('Você só pode editar compromissos criados na sua agenda pessoal (ou na agenda da Qualidade se você for Auditor).');
            return;
        }

        await updateDoc(doc(db, coll, eventoOriginal.id), dadosAtualizados);

        // --- Notificar participantes se data ou hora mudou ---
        await notifyParticipantsOfChange(
            eventoOriginal,
            dadosAtualizados,
            userData.name || currentUser.displayName || 'Usuário',
            coll
        );

        // --- Propagar mudança de data/hora ao documento original (se esta é uma cópia de aceite) ---
        // e para todas as outras cópias de outros participantes
        const dateChanged = dadosAtualizados.data && dadosAtualizados.data !== eventoOriginal.data;
        const timeChanged = dadosAtualizados.horario && dadosAtualizados.horario !== eventoOriginal.horario;
        const descChanged = dadosAtualizados.descricao && dadosAtualizados.descricao !== eventoOriginal.descricao;

        if ((dateChanged || timeChanged || descChanged) && eventoOriginal.sourceRef && eventoOriginal.sourceCollection) {
            try {
                const updates = {};
                if (dateChanged) updates.data = dadosAtualizados.data;
                if (timeChanged) updates.horario = dadosAtualizados.horario;
                if (descChanged) updates.descricao = dadosAtualizados.descricao;

                // Atualizar o documento ORIGINAL (na coleção de origem do convite)
                try {
                    await updateDoc(doc(db, eventoOriginal.sourceCollection, eventoOriginal.sourceRef), updates);
                    console.log('[SYNC-PESSOAL] Documento original atualizado:', eventoOriginal.sourceCollection, eventoOriginal.sourceRef);
                } catch (origErr) {
                    console.warn('[SYNC-PESSOAL] Sem permissão para atualizar original (normal para participantes comuns):', origErr.code);
                }

                // Atualizar outras cópias em agenda_pessoal (outros participantes que também aceitaram)
                const qOtherCopies = query(collection(db, 'agenda_pessoal'), where('sourceRef', '==', eventoOriginal.sourceRef));
                const snapOther = await getDocs(qOtherCopies);
                const promises = [];
                snapOther.forEach(d => {
                    if (d.id !== eventoOriginal.id) { // Não atualizar a própria cópia (já foi)
                        promises.push(updateDoc(doc(db, 'agenda_pessoal', d.id), updates));
                    }
                });
                if (promises.length > 0) {
                    await Promise.all(promises);
                    console.log(`[SYNC-PESSOAL] ${promises.length} outras cópias atualizadas.`);
                }
            } catch (syncErr) {
                console.error('[SYNC-PESSOAL] Erro ao propagar mudança:', syncErr);
            }
        }
    };

    const handleDeleteEvento = async (id, sourceColl, evento) => {
        const coll = sourceColl || getCollectionName('agenda_pessoal');
        const isSuper = isSuperUser(currentUser?.email);

        // Se for Agenda Compartilhada (Direção/Qualidade) e eu NÃO sou o dono (e não sou Super Usuário):
        // NÃO posso deletar o documento. Devo remover meu aceite (ou seja, sair do evento).
        if (coll !== getCollectionName('agenda_pessoal') && evento.createdBy !== currentUser.uid && !isSuper) {
            if (!window.confirm("Você não é o criador deste evento. Deseja RECUSAR/REMOVER sua participação?")) return;

            // Remover meu UID do array participantesUids
            await updateDoc(doc(db, coll, id), {
                participantesUids: arrayRemove(currentUser.uid),
                participantes: arrayRemove(userData.agendaName || userData.name)
            });
            return;
        }

        // REMOVIDO: Confirmação dupla, pois o modal já pergunta.

        try {
            // 1. AÇÃO PRINCIPAL: Excluir o documento clicado
            await deleteDoc(doc(db, coll, id));

            // 2. AÇÃO SECUNDÁRIA (SuperAdmin): Limpeza profunda de cópias em outras coleções
            if (isSuper) {
                const originalId = evento?.originalEventId || evento?.sourceRef || (id.startsWith('ext-') ? id.replace('ext-', '') : id);
                const originColl = evento?.sourceCollection || 'agendaDirecao';

                // Deleta a cópia gerada no padrão antigo (ext-ID)
                try {
                    await deleteDoc(doc(db, 'agenda_pessoal', 'ext-' + originalId));
                } catch (e) { }

                // Deleta o original se existir
                try {
                    await deleteDoc(doc(db, originColl, originalId));
                } catch (e) { }

                // Buscar e deletar clones aleatórios perdidos na agendaDirecao (sourceRef == originalId)
                try {
                    const qDir = query(collection(db, 'agendaDirecao'), where('sourceRef', '==', originalId));
                    const snapDir = await getDocs(qDir);
                    snapDir.forEach(async (d) => await deleteDoc(doc(db, 'agendaDirecao', d.id)));
                } catch (e) { }

                // Buscar e deletar clones aleatórios perdidos na agenda_pessoal (originalEventId == originalId)
                try {
                    const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', originalId));
                    const snapPes = await getDocs(qPes);
                    snapPes.forEach(async (d) => await deleteDoc(doc(db, 'agenda_pessoal', d.id)));
                } catch (e) { }
            } else if (evento?.originalEventId && evento?.sourceCollection) {
                // Usuário comum: apenas remove seu aceite da origem
                try {
                    const myAgendaName = userData?.agendaName || userData?.name;
                    const originalRef = doc(db, evento.sourceCollection, evento.originalEventId);

                    await updateDoc(originalRef, {
                        aceitePor: arrayRemove(myAgendaName),
                        aceitePorUids: arrayRemove(currentUser.uid)
                    });
                } catch (errOriginal) {
                    console.log('Clean-up do original falhou:', errOriginal);
                }
            }

            alert('Evento removido com sucesso!');
            setIsModalOpen(false);
        } catch (error) {
            console.error("Erro ao excluir evento:", error);
            alert("Erro ao remover evento local: " + error.message);
        }
    };

    // Função para reagendar evento recusado
    const handleReagendar = (eventoId, eventoCollection) => {
        // Buscar o evento para editar
        const evento = eventos.find(e => e.id === eventoId);
        if (evento) {
            setEditingEvento(evento);
            setIsModalOpen(true);
        } else {
            alert('Evento não encontrado. Pode ter sido excluído.');
        }
    };

    if (loading) return <div className="p-4">Carregando sua agenda...</div>;

    const renderMobileLayout = () => {
        return (
            <div className="agenda-mobile-list">
                <div className="agenda-controls mb-3" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                    <button onClick={handlePrevMonth} className="btn btn-sm btn-outline-secondary">&lt;</button>
                    <h4 className="mb-0 text-capitalize">{format(currentDate, 'MMMM yyyy', { locale: ptBR })}</h4>
                    <button onClick={handleNextMonth} className="btn btn-sm btn-outline-secondary">&gt;</button>
                </div>

                {monthDays.filter(day => day.getMonth() === currentDate.getMonth()).map(day => {
                    const dayString = day.toISOString().split('T')[0];
                    const itemsDoDia = eventos.filter(e => e.data === dayString).sort((a, b) => (a.horario || '').localeCompare(b.horario || ''));

                    if (itemsDoDia.length === 0) return null;

                    return (
                        <div key={dayString} className="mobile-day-group" style={{ marginBottom: '15px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '10px' }}>
                                <span style={{ fontWeight: 'bold', color: '#00897b', textTransform: 'capitalize' }}>{format(day, 'EEEE, dd/MM', { locale: ptBR })}</span>
                            </div>

                            <div className="mobile-events-list">
                                {itemsDoDia.map(item => {
                                    // Status visual para mobile
                                    let bgColor = '#f9f9f9';
                                    if (item.status === 'Feito') {
                                        bgColor = '#d4edda';
                                    } else if (item.status === 'Não Realizado' || item.justificativaNaoRealizado) {
                                        bgColor = '#f8d7da';
                                    } else {
                                        const hoje = new Date();
                                        hoje.setHours(0, 0, 0, 0);
                                        const dataItem = new Date(item.data + 'T00:00:00');
                                        if (dataItem < hoje && (!item.status || item.status === 'Agendado')) {
                                            bgColor = '#f8d7da';
                                        }
                                    }
                                    return (
                                        <div
                                            key={item.id}
                                            className="mobile-event-card"
                                            style={{
                                                padding: '10px',
                                                backgroundColor: bgColor,
                                                marginBottom: '8px',
                                                borderRadius: '6px',
                                                borderLeft: `4px solid ${item.sourceLabel === 'Direção' ? '#ffc107' : (item.sourceLabel === 'Homecare' ? '#6f42c1' : (item.sourceLabel === 'Pessoal' ? '#00897b' : '#28a745'))}`
                                            }}
                                            onClick={() => { setEditingEvento(item); setIsModalOpen(true); }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <strong>{item.horario}</strong>
                                                <span className="badge bg-light text-dark">{item.sourceLabel}</span>
                                            </div>
                                            <div style={{ marginTop: '5px' }}>{item.descricao || item.auditoria}</div>
                                            {item.unidade && <div className="small text-muted">{item.unidade} - {item.setor}</div>}
                                            <div className="small mt-1">Status: {item.status}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {eventos.filter(e => {
                    const d = new Date(e.data + 'T00:00:00');
                    return d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
                }).length === 0 && (
                        <div className="text-center p-4 text-muted">Nenhum agendamento neste mês.</div>
                    )}
            </div>
        );
    };
    if (loading) {
        return <div className="text-center p-5"><strong>Carregando Minha Agenda...</strong></div>;
    }

    // ── Visual igual ao da Agenda da Qualidade: cabeçalho fixo, barra de controles e cartões ──
    const ORIGENS = { Pessoal: '#00897b', 'E-mail': '#2563eb', 'Direção': '#f59e0b', Qualidade: '#10b981' };
    const origemDe = item => (item.origem === 'email' ? 'E-mail' : item.sourceLabel || 'Pessoal');
    const statusClasse = item => {
        if (item.status === 'Feito') return 'status-feito';
        if (item.status === 'Não Realizado' || item.justificativaNaoRealizado) return 'status-nao-realizado';
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
        if (new Date(item.data + 'T00:00:00') < hoje && (!item.status || item.status === 'Agendado')) return 'status-nao-realizado';
        return '';
    };
    const passaFiltro = item => {
        if (filtroOrigem !== 'Todos' && origemDe(item) !== filtroOrigem) return false;
        if (!filtroRealizar && !filtroNaoRealizados) return true;
        const cls = statusClasse(item);
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
        const futuro = new Date(item.data + 'T00:00:00') >= hoje && item.status !== 'Feito' && item.status !== 'Cancelado';
        return (filtroRealizar && futuro) || (filtroNaoRealizados && cls === 'status-nao-realizado');
    };
    const eventosVisiveis = eventos.filter(passaFiltro);
    const btnBase = { padding: '8px 14px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', color: '#fff' };
    const navBtn = { background: '#f1f5f9', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', color: '#475569', borderRadius: '6px', padding: '4px 10px' };
    const rotuloFiltro = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, color: '#444' };

    return (
        <div className="agenda-container agenda-pessoal" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', '--ag-fundo': tema.fundo, '--ag-destaque': tema.destaque, '--ag-texto': textoPara(tema.fundo) }}>
            {/* === CABEÇALHO E FILTROS CONGELADOS === */}
            <div style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'transparent', padding: '20px 20px 10px 20px', marginLeft: '-20px', marginRight: '-20px', marginTop: '-20px', borderBottom: '1px solid #e2e8f0' }}>
                <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                        <h2 style={{ color: '#1e293b', margin: 0, fontSize: '1.4rem', fontWeight: '800' }}>Minha Agenda</h2>
                        <span style={{ color: '#64748b', fontSize: 13 }}>Seus compromissos: pessoais, da Direção, da Qualidade e os convites que chegam por e-mail.</span>
                    </div>
                    <div className="header-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <SeletorTema tema={tema} onSalvar={salvarTema} />
                        <button style={{ ...btnBase, backgroundColor: 'var(--ag-destaque)' }} onClick={() => { setEditingEvento(null); setIsModalOpen(true); }}>+ Novo Agendamento</button>
                    </div>
                </div>

                {!isMobile && (
                    <div className="agenda-controls" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '15px', backgroundColor: '#fff', padding: '10px 20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', marginBottom: '10px' }}>
                        <div className="week-navigation" style={{ display: 'flex', alignItems: 'center', gap: '5px', margin: 0 }}>
                            <button onClick={handlePrevMonth} style={navBtn}>&lt;</button>
                            <span style={{ fontSize: '15px', fontWeight: '700', minWidth: '140px', textAlign: 'center', textTransform: 'capitalize', color: '#334155' }}>
                                {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                            </span>
                            <button onClick={handleNextMonth} style={navBtn}>&gt;</button>
                            <button onClick={() => setCurrentDate(new Date())} style={{ ...navBtn, fontWeight: 600, fontSize: 12.5, marginLeft: 6 }}>Hoje</button>
                        </div>
                        <div className="filter-area" style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={rotuloFiltro}><input type="checkbox" checked={filtroRealizar} onChange={e => setFiltroRealizar(e.target.checked)} style={{ accentColor: '#10b981' }} /> A Realizar</label>
                            <label style={rotuloFiltro}><input type="checkbox" checked={filtroNaoRealizados} onChange={e => setFiltroNaoRealizados(e.target.checked)} style={{ accentColor: '#ef4444' }} /> Não Realizados</label>
                        </div>
                        <div className="unidade-tabs" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {['Todos', ...Object.keys(ORIGENS)].map(o => {
                                const ativo = filtroOrigem === o;
                                const cor = ORIGENS[o] || '#334155';
                                return (
                                    <button key={o} onClick={() => setFiltroOrigem(o)}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: `1px solid ${ativo ? cor : '#e2e8f0'}`, background: ativo ? cor : '#fff', color: ativo ? '#fff' : '#334155' }}>
                                        {!ativo && o !== 'Todos' && <span style={{ width: 8, height: 8, borderRadius: '50%', background: cor }} />}
                                        {o}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <main className="agenda-main" style={{ flex: 1, overflowY: 'auto' }}>
                {isMobile ? renderMobileLayout() : (
                    <>
                        <div className="agenda-week-header">
                            <div>Segunda</div><div>Terça</div><div>Quarta</div><div>Quinta</div><div>Sexta</div>
                        </div>
                        <div className="agenda-grid-monthly">
                            {monthDays.map((day) => {
                                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                                const dayString = day.toISOString().split('T')[0];
                                const ehHoje = dayString === new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
                                const itemsDoDia = eventosVisiveis.filter(e => e.data === dayString).sort((a, b) => (a.horario || '').localeCompare(b.horario || ''));
                                return (
                                    <div key={day.toISOString()} className={`day-cell ${isCurrentMonth ? '' : 'not-current-month'}`} style={ehHoje ? { boxShadow: 'inset 0 0 0 2px var(--ag-destaque)' } : undefined}>
                                        <div className="day-cell-header">
                                            <span className="day-number" style={ehHoje ? { background: 'var(--ag-destaque)', color: '#fff', borderRadius: 10, padding: '0 7px' } : undefined}>{day.getDate()}</span>
                                        </div>
                                        <div className="day-cell-body">
                                            {itemsDoDia.length === 0 && <div className="empty-day"></div>}
                                            {itemsDoDia.map(item => {
                                                const origem = origemDe(item);
                                                const cor = ORIGENS[origem] || '#64748b';
                                                const pessoas = item.participantesNomes?.length ? item.participantesNomes.join(', ') : (item.participantes || '');
                                                return (
                                                    <div key={`${item.sourceColl}-${item.id}`} className={`auditoria-card ${statusClasse(item)}`}
                                                        style={{ borderLeftColor: isCurrentMonth ? cor : '#aaa', ...(item.status === 'Cancelado' ? { opacity: 0.6, textDecoration: 'line-through' } : {}) }}
                                                        onClick={() => { setEditingEvento(item); setIsModalOpen(true); }}
                                                        title={`${origem}: ${item.descricao || item.auditoria || ''}`}>
                                                        <div className="auditoria-card-header">
                                                            <strong>{item.horario}</strong> - {item.descricao || item.auditoria}
                                                        </div>
                                                        <div className="auditoria-card-body">
                                                            <p><strong>Origem:</strong> {origem}{item.link && item.status !== 'Cancelado' ? ' · 🎥 reunião online' : ''}</p>
                                                            {item.setor && <p><strong>Setor:</strong> {item.setor}</p>}
                                                            {pessoas && <p style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}><strong>Participantes:</strong> {pessoas}</p>}
                                                        </div>
                                                        {item.justificativaNaoRealizado && <div className="justificativa-balao"><strong>Motivo:</strong> {item.justificativaNaoRealizado}</div>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </main>

            <AddAuditoriaModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddAuditoria={handleAddEvento}
                onUpdateAuditoria={handleUpdateEvento}
                onDeleteAuditoria={(id) => handleDeleteEvento(id, editingEvento?.sourceColl, editingEvento)}
                editingAuditoria={editingEvento}
                type="pessoal"
                unidadesConfig={emptyUnidadesConfig}
                auditoresConfig={allUsersConfig}
                userName={userData?.name || currentUser?.displayName || ''}
                canPublishQualidade={isAuditor}
                canPublishHomecare={true}
            />
        </div>
    );
}

export default MinhaAgenda;
